import { useSelector } from "react-redux";
import { Store } from "redux";
import { get } from "lodash";
import { usePawsBraintreeCC } from "@urp/amplitude-experiments";
import { getBraintreeCCToggle } from "../redux/toggles";

/**
 * Returns whether the Braintree CC deposit/withdrawal experience is enabled
 * for the current user, applying both the SGW kill switch and the Amplitude
 * feature flag.
 *
 * Resolution order:
 *  1. SGW toggle `pawsBraintreeCC` is the master kill switch. If OFF, return
 *     false regardless of Amplitude.
 *  2. If the `useAmplitude` CAPI toggle is OFF, the Amplitude SDK isn't running
 *     and we fall back to SGW-only behavior (return true) to avoid breaking
 *     existing users when the experiment infrastructure isn't available.
 *  3. Otherwise, return whether the Amplitude experiment
 *     `tc-1046-use-pawsbraintreecc` resolves to "treatment" for this user.
 */
export const useIsBraintreeEnabled = (): boolean => {
  const isSgwBraintreeEnabled = useSelector(getBraintreeCCToggle);
  const isAmplitudeEnabled = useSelector<Store, boolean>((store) =>
    get(store, "capi.featureToggles.useAmplitude", false)
  );
  const isAmplitudeBraintreeTreatment = usePawsBraintreeCC();

  if (!isSgwBraintreeEnabled) return false;
  if (!isAmplitudeEnabled) return true;
  return isAmplitudeBraintreeTreatment;
};

export default useIsBraintreeEnabled;
